import React from 'react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { ItemWarehouseReceivingSection } from './ItemWarehouseReceivingSection';
import { ProcurementItem } from '@/types/database/procurement-items';
import { calculateNetPayableAmount, calculateAveragePricePerUnit } from '@/utils/procurementCalculations';
import { formatCurrency } from '@/lib/currency';

interface WarehouseInspectionSectionProps {
  procurement: any;
}

export const WarehouseInspectionSection: React.FC<WarehouseInspectionSectionProps> = ({
  procurement
}) => {
  const warehouse = procurement.warehouse;
  
  // Determine inspection status
  const getInspectionStatus = () => {
    if (warehouse?.warehouse_confirmed_by) {
      return 'confirmed';
    } else if (warehouse?.warehouse_rejected_by) {
      return 'rejected';
    } else {
      return 'pending';
    }
  };

  const inspectionStatus = getInspectionStatus();

  const getStatusIcon = () => {
    switch (inspectionStatus) {
      case 'confirmed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />;
    }
  };

  const getStatusBadge = () => {
    switch (inspectionStatus) {
      case 'confirmed':
        return <Badge variant="default" className="bg-green-100 text-green-800">Confirmed</Badge>;
      case 'rejected':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="outline">Pending Inspection</Badge>;
    }
  };

  const calculateDamageImpact = () => {
    if (!warehouse?.damage_percentage || !procurement.total_quantity) return null;
    
    const damageAmount = (procurement.total_quantity * warehouse.damage_percentage) / 100;
    return {
      damageAmount,
      isDamageSignificant: warehouse.damage_percentage > 10
    };
  };

  const damageImpact = calculateDamageImpact();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          {getStatusIcon()}
          Warehouse Inspection
        </h3>
        {getStatusBadge()}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Inspection Status Details */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Inspection Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {inspectionStatus === 'confirmed' && warehouse && (
              <>
                <div>
                  <p className="text-sm font-medium text-green-600">✓ Warehouse Confirmed</p>
                  <p className="text-xs text-muted-foreground">
                    {warehouse.warehouse_confirmation_date && 
                      format(new Date(warehouse.warehouse_confirmation_date), 'PPp')
                    }
                  </p>
                </div>
                {warehouse.net_kg_payable && (
                  <div>
                    <p className="text-sm font-medium">Net Payable</p>
                    <p className="text-sm">{warehouse.net_kg_payable.toFixed(2)} kg</p>
                    <p className="text-sm font-semibold text-green-700">
                      {formatCurrency(calculateNetPayableAmount(procurement), procurement.currency || 'ETB')}
                    </p>
                  </div>
                )}
              </>
            )}

            {inspectionStatus === 'rejected' && warehouse && (
              <>
                <div>
                  <p className="text-sm font-medium text-red-600">✗ Warehouse Rejected</p>
                  <p className="text-xs text-muted-foreground">
                    {warehouse.warehouse_rejection_date && 
                      format(new Date(warehouse.warehouse_rejection_date), 'PPp')
                    }
                  </p>
                </div>
                {warehouse.warehouse_rejection_reason && (
                  <div>
                    <p className="text-sm font-medium">Rejection Reason</p>
                    <p className="text-sm capitalize">{warehouse.warehouse_rejection_reason.replace(/_/g, ' ')}</p>
                  </div>
                )}
              </>
            )}

            {inspectionStatus === 'pending' && (
              <div>
                <p className="text-sm font-medium text-yellow-600">⏳ Pending Inspection</p>
                <p className="text-xs text-muted-foreground">
                  Awaiting warehouse confirmation
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quality Assessment */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Quality Assessment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {warehouse?.sample_taken !== undefined && (
              <div>
                <p className="text-sm font-medium">Sample Taken</p>
                <p className="text-sm">{warehouse.sample_taken ? 'Yes' : 'No'}</p>
              </div>
            )}

            {warehouse?.damage_percentage !== undefined && (
              <div>
                <p className="text-sm font-medium flex items-center gap-1">
                  Damage Percentage
                  {damageImpact?.isDamageSignificant && (
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  )}
                </p>
                <p className={`text-sm ${damageImpact?.isDamageSignificant ? 'text-yellow-600 font-medium' : ''}`}>
                  {warehouse.damage_percentage.toFixed(1)}%
                </p>
                {damageImpact && (
                  <p className="text-xs text-muted-foreground">
                    ≈ {damageImpact.damageAmount.toFixed(2)} kg damaged
                  </p>
                )}
              </div>
            )}

            {warehouse?.net_kg_payable && procurement.total_quantity && (
              <div>
                <p className="text-sm font-medium">Quantity Comparison</p>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span>Original:</span>
                    <span>{procurement.total_quantity.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Net Payable:</span>
                    <span>{warehouse.net_kg_payable.toFixed(2)} kg</span>
                  </div>
                  <div className="flex justify-between font-semibold text-green-700 border-t pt-1 mt-1">
                    <span>Amount to Pay:</span>
                    <span>{formatCurrency(calculateNetPayableAmount(procurement), procurement.currency || 'ETB')}</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground border-t pt-1">
                    <span>Difference:</span>
                    <span>{(procurement.total_quantity - warehouse.net_kg_payable).toFixed(2)} kg</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Inspection Notes */}
      {warehouse?.inspection_notes && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Inspection Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm bg-muted p-3 rounded-md">{warehouse.inspection_notes}</p>
          </CardContent>
        </Card>
      )}

      {/* High Damage Warning */}
      {damageImpact?.isDamageSignificant && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
            <p className="text-sm font-medium text-yellow-800">High Damage Alert</p>
          </div>
          <p className="text-sm text-yellow-700 mt-1">
            This procurement has significant damage ({warehouse?.damage_percentage.toFixed(1)}%). 
            Please review the inspection details carefully.
          </p>
        </div>
      )}

      {/* Per-Item Warehouse Receiving Breakdown */}
      {procurement.items && (procurement.items as ProcurementItem[]).length > 0 && (
        <ItemWarehouseReceivingSection 
          items={procurement.items as ProcurementItem[]}
          currency={procurement.currency || 'ETB'}
          averagePricePerUnit={calculateAveragePricePerUnit(procurement)}
        />
      )}
    </div>
  );
};
